/**
 * Contact Page
 *
 * Contact form for users to reach out to the Have-Done team.
 * Uses reusable form validation components.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail, Send } from 'lucide-react';
import { useSubmitContactForm, type ContactFormData } from '@have-done/shared';
import { Footer } from './Footer';
import {
  FormInput,
  FormTextarea,
  FormSelect,
  FormError,
  FormSuccess,
  validationRules,
  validateField,
  type SelectOption,
} from './FormComponents';

const topicOptions: SelectOption[] = [
  { value: 'support', label: 'Support', description: 'Help with your account or the app' },
  { value: 'feedback', label: 'Feedback', description: 'Suggestions or feature requests' },
  { value: 'business', label: 'Business', description: 'Partnerships or business inquiries' },
  { value: 'other', label: 'Other', description: 'Anything else' },
];

interface FieldErrors {
  name?: string;
  email?: string;
  message?: string;
}

export function ContactPage() {
  const submitContactForm = useSubmitContactForm();

  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    email: '',
    topic: 'feedback',
    message: '',
  });
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear field error when user starts typing
    if (fieldErrors[name as keyof FieldErrors]) {
      setFieldErrors((prev) => ({ ...prev, [name]: undefined }));
    }

    // Clear global error state when user starts typing again
    if (submitStatus === 'error') {
      setSubmitStatus('idle');
      setErrorMessage('');
    }
  };

  const validateForm = (): boolean => {
    const errors: FieldErrors = {};

    // Validate name
    const nameError = validateField(formData.name, [
      validationRules.required('Name'),
      validationRules.minLength(2, 'Name'),
      validationRules.maxLength(100, 'Name'),
    ]);
    if (nameError) errors.name = nameError;

    // Validate email
    const emailError = validateField(formData.email, [
      validationRules.required('Email'),
      validationRules.email(),
    ]);
    if (emailError) errors.email = emailError;

    // Validate message
    const messageError = validateField(formData.message, [
      validationRules.required('Message'),
      validationRules.minLength(10, 'Message'),
      validationRules.maxLength(5000, 'Message'),
    ]);
    if (messageError) errors.message = messageError;

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate all fields
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');
    setErrorMessage('');

    try {
      await submitContactForm({
        name: formData.name.trim(),
        email: formData.email.trim(),
        topic: formData.topic,
        message: formData.message.trim(),
      });
      setSubmitStatus('success');
      // Reset form
      setFormData({
        name: '',
        email: '',
        topic: 'feedback',
        message: '',
      });
      setFieldErrors({});
    } catch (error) {
      setSubmitStatus('error');
      setErrorMessage(
        error instanceof Error ? error.message : 'Something went wrong. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-bg-primary flex flex-col">
      <div className="mx-auto max-w-3xl w-full px-3 sm:px-4 py-8 flex-1">
        {/* Back link */}
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-text-secondary hover:text-text-primary mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to app
        </Link>

        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
            <Mail className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-display font-bold text-text-primary">Contact Us</h1>
            <p className="text-sm text-text-secondary">We'd love to hear from you</p>
          </div>
        </div>

        {/* Success message */}
        {submitStatus === 'success' && (
          <div className="mb-8">
            <FormSuccess
              title="Message sent!"
              description="Thank you for reaching out. We'll get back to you as soon as possible."
            />
          </div>
        )}

        {/* Contact form */}
        <form onSubmit={handleSubmit} className="space-y-6" noValidate>
          {/* Name field */}
          <FormInput
            type="text"
            name="name"
            label="Name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Your name"
            disabled={isSubmitting}
            error={fieldErrors.name}
            required
          />

          {/* Email field */}
          <FormInput
            type="email"
            name="email"
            label="Email"
            value={formData.email}
            onChange={handleChange}
            placeholder="your@email.com"
            disabled={isSubmitting}
            error={fieldErrors.email}
            required
          />

          {/* Topic field */}
          <FormSelect
            name="topic"
            label="Topic"
            value={formData.topic}
            onChange={handleChange}
            options={topicOptions}
            disabled={isSubmitting}
          />

          {/* Message field */}
          <FormTextarea
            name="message"
            label="Message"
            value={formData.message}
            onChange={handleChange}
            placeholder="How can we help you?"
            rows={6}
            disabled={isSubmitting}
            error={fieldErrors.message}
            showCharCount
            maxChars={5000}
            required
          />

          {/* Global error message */}
          {submitStatus === 'error' && errorMessage && (
            <FormError message={errorMessage} />
          )}

          {/* Submit button */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-primary text-white font-medium hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <span className="animate-spin w-4 h-4 border-2 border-white/30 border-t-white rounded-full" />
                Sending...
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                Send Message
              </>
            )}
          </button>
        </form>

        {/* Alternative contact info */}
        <div className="mt-12 pt-8 border-t border-border-light">
          <h2 className="text-lg font-semibold text-text-primary mb-4">Other ways to reach us</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="bg-bg-secondary rounded-lg p-4">
              <h3 className="font-medium text-text-primary mb-1">General inquiries</h3>
              <a
                href="mailto:hello@have-done.app"
                className="text-sm text-primary hover:underline"
              >
                hello@have-done.app
              </a>
            </div>
            <div className="bg-bg-secondary rounded-lg p-4">
              <h3 className="font-medium text-text-primary mb-1">Privacy questions</h3>
              <a
                href="mailto:privacy@have-done.app"
                className="text-sm text-primary hover:underline"
              >
                privacy@have-done.app
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
}
